import React, { useState } from "react";
import { Flex, Box, IconButton, Text, Textarea } from "@chakra-ui/react";
import { FiX, FiSend } from "react-icons/fi";
import { ConversationView } from "newui/features/Conversation/components/ConversationView";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { Button } from "newui/components/Button";
import { useClarificationChat } from "../../hooks/useClarificationChat";
import type { Clarification } from "../../types/clarification.type";

interface ClarificationChatPanelProps {
  clarification: Clarification;
  hideThreadBar?: boolean;
  isOpen: boolean;
  readOnly?: boolean;
  rfxId: number;
  showOnlyChat?: boolean;
  showThreadRef?: boolean;
  onClose: () => void;
}

interface FirstMessageComposerProps {
  isLoading: boolean;
  onSend: (message: string) => void;
}

const FirstMessageComposer: React.FC<FirstMessageComposerProps> = ({
  onSend,
  isLoading,
}) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (message.trim() && !isLoading) {
      onSend(message.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Flex direction="column" h="full" p={4} gap={4}>
      <Flex direction="column" align="center" justify="center" flex="1" gap={3}>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          Start a discussion about this clarification. Messages here are
          informal and separate from the formal clarification response.
        </Text>
      </Flex>
      <Flex direction="column" gap={2}>
        <Textarea
          placeholder="Type your first message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          size="sm"
          resize="none"
          rows={3}
          isDisabled={isLoading}
        />
        <Flex justify="flex-end">
          <Button
            btntype="primary"
            onClick={handleSend}
            disabled={!message.trim() || isLoading}
            className="flex items-center gap-2 !px-4 !py-2"
          >
            <FiSend className="h-4 w-4" />
            Send
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const ClarificationChatPanel: React.FC<ClarificationChatPanelProps> = ({
  clarification,
  hideThreadBar = false,
  isOpen,
  rfxId,
  readOnly = false,
  showOnlyChat = false,
  showThreadRef = true,
  onClose,
}) => {
  const { conversation, isLoading, error, startChat, onConversationUpdated } =
    useClarificationChat(clarification.id, rfxId, isOpen);

  if (!isOpen) {
    return null;
  }

  return (
    <Flex direction="column" h="full" w="full">
      {!hideThreadBar && !showOnlyChat && <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="gray.50"
      >
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Discussion Thread
        </Text>
        <IconButton
          aria-label="Close chat panel"
          icon={<FiX />}
          size="sm"
          variant="ghost"
          onClick={onClose}
        />
      </Flex>}

      <Box flex="1" overflow="hidden">
        {isLoading && (
          <LoadingSpinner
            label="Loading chat..."
            fontSize="md"
            horizontal
            spinnerSize="md"
            verticalPadding={10}
          />
        )}

        {error && (
          <Flex align="center" justify="center" h="full" p={4}>
            <Text color="red.500" fontSize="sm" textAlign="center">
              {error}
            </Text>
          </Flex>
        )}

        {!isLoading && !error && conversation && (
          <ConversationView
            allowNewMessages={!readOnly}
            conversation={conversation}
            scrollOnLoad
            showBackButton={false}
            goBackOnMobile={() => {
              /* no-op: single conversation view */
            }}
            onSendingMessage={() => {
              /* no-op: handled internally by MessageComposer */
            }}
            onConversationUpdated={onConversationUpdated}
            showOnlyChat={showOnlyChat}
            showThreadRef={showThreadRef}
          />
        )}

        {!isLoading && !error && !conversation && !readOnly && (
          <FirstMessageComposer onSend={startChat} isLoading={isLoading} />
        )}

        {!isLoading && !error && !conversation && readOnly && (
          <Flex align="center" justify="center" h="full" p={4}>
            <Text color="gray.500" fontSize="sm" textAlign="center">
              No discussion thread for this clarification.
            </Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
